import { Dispatch, SetStateAction } from "react";
import { Bounce, Id, toast } from "react-toastify";
import { Exception } from 'sass';

export function useSuccessToast(title: string, autoClose: number = 5000): Id {
    const id = toast.success(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce
    });

    return id;
}

export function useErrorToast(title: string, autoClose: number = 5000): Id {
    const id = toast.error(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

export function useLoadingToast(title: string, autoClose: number = 5000): Id {
    const id = toast.loading(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

export function dissmissToast(id: Id) {
    toast.dismiss(id)
}

// Non-hook-named aliases: the use*Toast helpers above are plain functions,
// but their names trip the react-hooks lint rules when called from effects
// or handlers inside components. New call sites should prefer these.
export function showSuccessToast(title: string, autoClose: number = 5000): Id {
    return useSuccessToast(title, autoClose);
}

export function showInfoToast(title: string, autoClose: number = 8000): Id {
    const id = toast.info(title, {
        position: "top-right",
        autoClose: autoClose,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
    });

    return id;
}

interface ContextError { message: String, source: String, statusCode: number }

export async function withToasts<T>(
    asyncFunction: () => Promise<T>,
    loadingMessage: string,
    successMessage: string,
    errorMessage: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>
): Promise<T> {
    setIsLoading && setIsLoading(true);
    const toast = useLoadingToast(loadingMessage)

    try {
        const result = await asyncFunction()
        useSuccessToast(successMessage)
        return result;
    } catch (error) {
        const contextError = error as ContextError
        let compoundErrorMessage = errorMessage
        if (contextError && contextError.message) {
            if (errorMessage) {
                compoundErrorMessage += ': ' + contextError.message
            } else {
                compoundErrorMessage = contextError.message as string
            }
        }

        useErrorToast(compoundErrorMessage)
        throw error
    } finally {
        dissmissToast(toast)
        setIsLoading && setIsLoading(false)
    }
}